import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { AlertTriangle, Activity, Clock, LifeBuoy, Layers, ShieldCheck, Siren, Users, XCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { PaginationBar } from "@/components/PaginationBar";
import {
  CockpitColumns,
  CockpitEmpty,
  CockpitFilterBar,
  CockpitListShell,
  CockpitRow,
  CockpitSearch,
  CockpitChipGroup,
  HeroDivider,
  HeroMetric,
  InlineMeta,
  StatusDot,
} from "@/components/cockpit/CockpitShell";
import { CockpitPage } from "@/components/cockpit/CockpitPage";
import { SubPivot } from "@/components/cockpit/SubPivot";
import { useRole } from "@/data/RoleContext";
import { canAccessInstallsLive } from "@/data/executionDomain";
import { cn } from "@/lib/utils";
import { useCountedServerPagination } from "@/hooks/useServerPagination";
import { useExecutionEscalationsPage, useExecutionInstallRowsPage, useExecutionSummary } from "@/hooks/useExecutionServerPaging";
import type { ExecutionEscalationSignal, ExecutionInstallRow, ExecutionLifecycle } from "@/services/execution-page.service";
import type { EscalationSeverity } from "@/data/observability/escalationSignalsSelector";

type TabKey = "active" | "escalations";
type LifecycleFilter = ExecutionLifecycle;

const TAB_TO_URL: Record<TabKey, string> = {
  active: "active-installs",
  escalations: "escalation-signals",
};
const URL_TO_TAB: Record<string, TabKey> = Object.fromEntries(
  Object.entries(TAB_TO_URL).map(([k, v]) => [v, k as TabKey]),
) as Record<string, TabKey>;

const ACTIVE_TEMPLATE = "minmax(220px,1.8fr) 110px 110px 100px 90px 80px";
const LIFECYCLE_LABEL: Record<Exclude<LifecycleFilter, "all">, string> = {
  in_progress: "In progress",
  ready: "Ready",
  blocked: "Blocked",
};
const LIFECYCLE_BADGE_CLASS: Record<Exclude<LifecycleFilter, "all">, string> = {
  in_progress: "bg-success/15 text-success ring-1 ring-success/30",
  ready: "bg-warning/15 text-warning ring-1 ring-warning/30",
  blocked: "bg-destructive/15 text-destructive ring-1 ring-destructive/30",
};

function fmtAge(iso?: string, now: Date = new Date()): string {
  if (!iso) return "--";
  const ms = now.getTime() - new Date(iso).getTime();
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  const min = Math.round(ms / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  return `${Math.round(hr / 24)}d ago`;
}

function severityClass(sev: EscalationSeverity): string {
  if (sev === "high") return "bg-destructive/15 text-destructive ring-1 ring-destructive/30";
  if (sev === "medium") return "bg-warning/15 text-warning ring-1 ring-warning/30";
  return "bg-muted text-muted-foreground ring-1 ring-border/60";
}

function rowTone(row: ExecutionInstallRow): "good" | "warn" | "bad" | "default" {
  if (row.lifecycle === "blocked" || row.isStale || row.activePhaseStatus === "qa_failed") return "bad";
  if (row.awaitingQa || row.reconciliationPending) return "warn";
  if (row.lifecycle === "in_progress") return "good";
  return "default";
}

function phaseLabel(row: ExecutionInstallRow): string {
  if (row.lifecycle !== "in_progress") return "Not started";
  return row.activePhaseName ?? "complete";
}

function PortalToControls({ children }: { children: React.ReactNode }) {
  const [host, setHost] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setHost(document.getElementById("install-masthead-controls"));
  }, []);
  if (!host) return null;
  return createPortal(<>{children}</>, host);
}

function EmptyCard({ message }: { message: string }) {
  return <CockpitEmpty message={message} />;
}

function ServerActiveInstallsTab({
  rows,
  totalCount,
  loading,
  pagination,
  search,
  setSearch,
  filter,
  setFilter,
  summary,
}: {
  rows: ExecutionInstallRow[];
  totalCount: number;
  loading: boolean;
  pagination: ReturnType<typeof useCountedServerPagination>;
  search: string;
  setSearch: (value: string) => void;
  filter: LifecycleFilter;
  setFilter: (value: LifecycleFilter) => void;
  summary: ReturnType<typeof useExecutionSummary>["data"];
}) {
  const now = useMemo(() => new Date(), []);
  const activeCount = summary?.activeCount ?? totalCount;
  const inProgressCount = summary?.inProgressCount ?? 0;
  const blockedCount = summary?.blockedCount ?? 0;
  const readyCount = summary?.readyCount ?? 0;

  return (
    <div className="space-y-3">
      <CockpitFilterBar
        search={
          <CockpitSearch
            value={search}
            onChange={setSearch}
            placeholder="Search by customer, job ID, city, or installer"
          />
        }
        chips={
          <CockpitChipGroup
            value={filter}
            onChange={(v) => setFilter(v as LifecycleFilter)}
            options={[
              { value: "all", label: "All", count: activeCount },
              { value: "in_progress", label: "In progress", count: inProgressCount },
              { value: "blocked", label: "Blocked", count: blockedCount },
              { value: "ready", label: "Ready", count: readyCount },
            ]}
          />
        }
      />

      {loading && rows.length === 0 ? (
        <EmptyCard message="Loading active installs..." />
      ) : rows.length === 0 ? (
        <EmptyCard message="No installs match this search or filter." />
      ) : (
        <>
          <CockpitListShell>
            <CockpitColumns template={ACTIVE_TEMPLATE}>
              <span>Job</span>
              <span>Stage</span>
              <span>Phase</span>
              <span>Photos</span>
              <span>Checklist</span>
              <span className="whitespace-nowrap">Updated</span>
            </CockpitColumns>
            {rows.map((row) => {
              const blockers = row.issueCount + row.ticketCount + row.fieldFeedbackCount + row.substitutionCount;
              return (
                <CockpitRow key={row.jobId} template={ACTIVE_TEMPLATE} to={`/jobs/${row.jobId}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <StatusDot tone={rowTone(row)} />
                      <span className="text-sm font-medium text-foreground truncate">{row.customerName}</span>
                      {row.qaFailuresTotal > 0 && (
                        <Badge variant="outline" className="gap-1 text-[10px] text-destructive border-destructive/40 shrink-0">
                          <XCircle className="h-3 w-3" /> {row.qaFailuresTotal}
                        </Badge>
                      )}
                      {blockers > 0 && (
                        <Badge variant="outline" className="gap-1 text-[10px] text-warning border-warning/40 shrink-0">
                          <LifeBuoy className="h-3 w-3" /> {blockers}
                        </Badge>
                      )}
                    </div>
                    <InlineMeta items={[row.jobLabel, row.city, `Installer: ${row.installerName}`]} />
                    {row.blockerReasons.length > 0 && (
                      <p className="mt-1 text-[10px] text-muted-foreground truncate">
                        {row.blockerReasons.join(" · ")}
                      </p>
                    )}
                  </div>
                  <Badge className={cn("text-[10px] w-fit", LIFECYCLE_BADGE_CLASS[row.lifecycle])}>
                    {LIFECYCLE_LABEL[row.lifecycle]}
                  </Badge>
                  <p className="text-xs text-foreground capitalize truncate">{phaseLabel(row)}</p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {row.executionAvailable ? `${row.totalPhotosUploaded}/${row.totalPhotosRequired}` : "--"}
                  </p>
                  <p className="text-xs text-muted-foreground tabular-nums">
                    {row.executionAvailable ? `${row.totalChecklistChecked}/${row.totalChecklistTotal}` : "--"}
                  </p>
                  <p className="text-xs text-muted-foreground tabular-nums">{fmtAge(row.lastActivityAt, now)}</p>
                </CockpitRow>
              );
            })}
          </CockpitListShell>
          <PaginationBar {...pagination} />
        </>
      )}
    </div>
  );
}

function ServerEscalationsTab({
  signals,
  loading,
  pagination,
}: {
  signals: ExecutionEscalationSignal[];
  loading: boolean;
  pagination: ReturnType<typeof useCountedServerPagination>;
}) {
  if (loading && signals.length === 0) {
    return <Card className="p-8 text-center text-sm text-muted-foreground">Loading escalation clusters...</Card>;
  }
  if (!signals.length) {
    return (
      <Card className="p-8 text-center text-sm text-muted-foreground">
        No escalation clusters detected. Cluster signals require at least 3 related rows before they surface.
      </Card>
    );
  }
  return (
    <div className="space-y-2">
      <div className="text-[12px] text-muted-foreground">
        Cross-signal clusters derived from the active board. Pure read-only derivation, no automation or notifications.
      </div>
      {signals.map((signal) => (
        <Card key={signal.id} className="p-3">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                {signal.kind === "installer_overload" && <Users className="h-4 w-4 text-muted-foreground shrink-0" />}
                {signal.kind === "qa_backlog" && <ShieldCheck className="h-4 w-4 text-muted-foreground shrink-0" />}
                {signal.kind === "stalled_cluster" && <Clock className="h-4 w-4 text-muted-foreground shrink-0" />}
                {signal.kind === "risk_concentration" && <Layers className="h-4 w-4 text-muted-foreground shrink-0" />}
                <span className="font-semibold text-sm">{signal.headline}</span>
                <Badge className={cn("text-[10px] capitalize", severityClass(signal.severity))}>{signal.severity}</Badge>
                <Badge variant="outline" className="text-[10px]">
                  {signal.jobCount} job{signal.jobCount === 1 ? "" : "s"}
                </Badge>
              </div>
              <div className="mt-1 text-[12px] text-muted-foreground">{signal.detail}</div>
            </div>
          </div>
        </Card>
      ))}
      <PaginationBar {...pagination} />
    </div>
  );
}

export default function ExecutionInstallTodayServerPage({ embedded = false }: { embedded?: boolean } = {}) {
  const { role } = useRole();
  const [params, setParams] = useSearchParams();
  const paramKey = embedded ? "focus" : "tab";
  const tabParamRaw = params.get(paramKey) ?? "";
  const tab: TabKey = URL_TO_TAB[tabParamRaw] ?? "active";

  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState<LifecycleFilter>("all");
  const installPagination = useCountedServerPagination(25);
  const escalationPagination = useCountedServerPagination(10);
  const summary = useExecutionSummary({ enabled: true });
  const installPage = useExecutionInstallRowsPage({
    enabled: tab === "active",
    page: installPagination.page,
    pageSize: installPagination.pageSize,
    search,
    lifecycle: filter,
  });
  const escalationPage = useExecutionEscalationsPage({
    enabled: tab === "escalations",
    page: escalationPagination.page,
    pageSize: escalationPagination.pageSize,
  });

  const setInstallTotalItems = installPagination.setTotalItems;
  const setEscalationTotalItems = escalationPagination.setTotalItems;
  const resetInstallPagination = installPagination.reset;

  useEffect(() => {
    setInstallTotalItems(installPage.count);
  }, [installPage.count, setInstallTotalItems]);

  useEffect(() => {
    setEscalationTotalItems(escalationPage.count);
  }, [escalationPage.count, setEscalationTotalItems]);

  useEffect(() => {
    resetInstallPagination();
  }, [filter, search, resetInstallPagination]);

  if (!canAccessInstallsLive(role)) {
    return (
      <div className="max-w-3xl mx-auto p-8">
        <Card className="p-6 text-sm text-muted-foreground">You don't have access to Install Today.</Card>
      </div>
    );
  }

  const setTab = (nextTab: TabKey) => {
    const next = new URLSearchParams(params);
    if (nextTab === "active") next.delete(paramKey);
    else next.set(paramKey, TAB_TO_URL[nextTab]);
    setParams(next, { replace: true });
  };

  const summaryData = summary.data;
  const inProgress = summaryData?.inProgressCount ?? 0;
  const blockedCount = summaryData?.blockedCount ?? 0;
  const readyCount = summaryData?.readyCount ?? 0;
  const awaitingQa = summaryData?.awaitingQaCount ?? 0;
  const escalationCount = summaryData?.escalationCount ?? escalationPage.count;
  const highSeverity = summaryData?.highEscalationCount ?? 0;
  const mediumSeverity = summaryData?.mediumEscalationCount ?? 0;
  const lowSeverity = summaryData?.lowEscalationCount ?? 0;

  const headerRight = tab === "active" ? (
    <>
      <HeroMetric label="In Progress" value={inProgress} hero />
      <HeroDivider />
      <HeroMetric label="Ready" value={readyCount} tone={readyCount > 0 ? "good" : "default"} />
      <HeroDivider />
      <HeroMetric label="Blocked" value={blockedCount} tone={blockedCount > 0 ? "bad" : "default"} />
      <HeroDivider />
      <HeroMetric label="Awaiting QA" value={awaitingQa} tone={awaitingQa > 0 ? "warn" : "default"} />
    </>
  ) : (
    <>
      <HeroMetric label="Signals" value={escalationCount} hero />
      <HeroDivider />
      <HeroMetric label="High" value={highSeverity} tone={highSeverity > 0 ? "bad" : "default"} />
      <HeroDivider />
      <HeroMetric label="Medium" value={mediumSeverity} tone={mediumSeverity > 0 ? "warn" : "default"} />
      <HeroDivider />
      <HeroMetric label="Low" value={lowSeverity} />
    </>
  );

  const body = (
    <div className="space-y-4">
      <SubPivot<TabKey>
        value={tab}
        onChange={setTab}
        revealKey="exec-focus"
        ariaLabel="Execute focus"
        options={[
          {
            id: "active",
            label: "Active",
            sub: `${inProgress} in progress · ${blockedCount} blocked`,
            icon: <Activity className="h-3.5 w-3.5" />,
          },
          {
            id: "escalations",
            label: "Escalations",
            sub: `${escalationCount} signals`,
            icon: <Siren className="h-3.5 w-3.5" />,
          },
        ]}
      />
      <div className="pt-2">
        {tab === "active" ? (
          <ServerActiveInstallsTab
            rows={installPage.data}
            totalCount={installPage.count}
            loading={installPage.loading}
            pagination={installPagination}
            search={search}
            setSearch={setSearch}
            filter={filter}
            setFilter={setFilter}
            summary={summaryData}
          />
        ) : (
          <ServerEscalationsTab
            signals={escalationPage.data}
            loading={escalationPage.loading}
            pagination={escalationPagination}
          />
        )}
      </div>
    </div>
  );

  if (embedded) {
    return (
      <>
        <PortalToControls>{headerRight}</PortalToControls>
        {body}
      </>
    );
  }

  return (
    <CockpitPage title="Active Installs" maxWidth="7xl" controls={headerRight}>
      {body}
    </CockpitPage>
  );
}
